import { useCallback, useEffect, useMemo, useState } from "react";
import AttachmentsModal from "../../../AttachmentsModal";
import { ComboBox, DrawerTabs, SideDrawerShell, EntityAttachmentsTab } from "../../common/components";
import { epicsApi } from "../api/epicsApi";

const STATUS_OPTIONS = [
  "Planning",
  "In-Progress",
  "Regression Testing",
  "UAT Released",
  "Staging Released",
  "Live",
  "Closed",
];

const FREQUENCY_OPTIONS = ["Onetime", "Weekly", "Monthly"];
const RELEASE_ENVS = ["UAT", "Staging", "Production"];

function toInputDate(value) {
  if (!value) return "";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function fmtDateTime(value) {
  if (!value) return "—";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function asNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function createInitialForm(epic) {
  return {
    project_id: epic?.project_id || "",
    epic_name: epic?.epic_name || "",
    description: epic?.description || "",
    recurrence_frequency: epic?.recurrence_frequency || "Onetime",
    estimated_hours: epic?.estimated_hours != null ? String(epic.estimated_hours) : "0",
    start_date: toInputDate(epic?.start_date),
    end_date: toInputDate(epic?.end_date),
    status: epic?.status || "Planning",
  };
}

function createInitialReleasePlans() {
  return RELEASE_ENVS.reduce((acc, env) => {
    acc[env] = { enabled: false, planned_release_date: "" };
    return acc;
  }, {});
}

function createInitialTeamDraft() {
  return {
    team_id: "",
    start_date: "",
    end_date: "",
    planned_hours: "",
  };
}

export default function EpicDrawer({
  open,
  mode,
  epic,
  projects,
  readOnly = false,
  saving,
  onClose,
  onSave,
  showToast,
}) {
  const [form, setForm] = useState(createInitialForm(epic));
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState("details");

  const [teams, setTeams] = useState([]);
  const [teamPlans, setTeamPlans] = useState([]);
  const [teamDraft, setTeamDraft] = useState(createInitialTeamDraft());
  const [teamError, setTeamError] = useState("");
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamSaving, setTeamSaving] = useState(false);
  const [teamDeletingId, setTeamDeletingId] = useState("");

  const [releasePlans, setReleasePlans] = useState(createInitialReleasePlans());
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releaseSaving, setReleaseSaving] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateSaving, setTemplateSaving] = useState(false);

  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistUpdatingId, setChecklistUpdatingId] = useState("");
  const [attachmentModalItem, setAttachmentModalItem] = useState(null);

  const isEdit = mode === "edit";
  const epicId = epic?.id || "";

  useEffect(() => {
    if (!open) return;
    setForm(createInitialForm(epic));
    setErrors({});
    setActiveTab("details");
    setTeamDraft(createInitialTeamDraft());
    setTeamError("");
    setSelectedTemplateId("");
    if (!isEdit) {
      setTeamPlans([]);
      setChecklistItems([]);
      setReleasePlans(createInitialReleasePlans());
    }
  }, [open, epic, isEdit]);

  const projectOptions = useMemo(() => {
    // Edit mode: restrict the picker to projects under the epic's own
    // customer — reassigning across customers isn't supported (it would
    // also require cascading tasks' customer_id, not just project_id).
    const scoped = isEdit && epic?.customer_id
      ? (projects || []).filter((p) => p.customer_id === epic.customer_id)
      : (projects || []);
    const options = scoped.map((p) => ({
      value: p.id,
      label: `${p.project_name}${p.customer_name ? ` (${p.customer_name})` : ""}`,
    }));
    // `projects` is active_only=true — inject the epic's current project as a
    // synthetic option if it's missing (e.g. closed), same gap already fixed
    // for the Task drawer's projectOptions. Without this the field shows
    // blank for an epic on a closed project.
    if (isEdit && epic?.project_id && !options.find((o) => o.value === epic.project_id)) {
      options.push({
        value: epic.project_id,
        label: `${epic.project_name || "Current project"}${epic.customer_name ? ` (${epic.customer_name})` : ""}`,
      });
    }
    return options;
  }, [projects, isEdit, epic]);

  const teamOptions = useMemo(
    () =>
      (teams || []).map((t) => ({
        value: t.id,
        label: t.team_name || "—",
      })),
    [teams]
  );

  const totalPlannedHours = useMemo(
    () => teamPlans.reduce((sum, row) => sum + asNumber(row.planned_hours), 0),
    [teamPlans]
  );

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const setReleaseField = (env, key, value) => {
    setReleasePlans((prev) => ({
      ...prev,
      [env]: { ...prev[env], [key]: value },
    }));
  };

  const validate = () => {
    const next = {};
    if (!form.project_id) next.project_id = "Project is required";
    if (!form.epic_name.trim()) next.epic_name = "Epic name is required";
    if (!form.recurrence_frequency) next.recurrence_frequency = "Frequency is required";
    if (!form.start_date) next.start_date = "Start date is required";
    if (!form.end_date) next.end_date = "End date is required";
    if (!form.status) next.status = "Status is required";
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      next.end_date = "End date must be on/after start date";
    }
    const est = Number(form.estimated_hours);
    if (Number.isNaN(est) || est < 0) {
      next.estimated_hours = "Estimated hours must be 0 or more";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateTeamDraft = () => {
    if (!teamDraft.team_id) return "Team is required";
    if (!teamDraft.start_date) return "Start date is required";
    if (!teamDraft.end_date) return "End date is required";
    const planned = Number(teamDraft.planned_hours);
    if (!Number.isFinite(planned) || planned <= 0) return "Planned hours must be > 0";
    if (teamDraft.end_date < teamDraft.start_date) return "Team end date must be on/after start date";
    if (form.start_date && teamDraft.start_date < form.start_date) return "Team start date must be >= epic start date";
    if (form.end_date && teamDraft.end_date > form.end_date) return "Team end date must be <= epic end date";
    const estimate = Number(form.estimated_hours || 0);
    if (totalPlannedHours + planned > estimate) {
      return "Team planned hours total cannot exceed epic estimated hours";
    }
    return "";
  };

  const loadTeamPlans = useCallback(async () => {
    if (!epicId) return;
    setTeamLoading(true);
    try {
      const data = await epicsApi.listTeamPlans(epicId);
      setTeamPlans(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast?.(err?.message || "Failed to load team plans", "error");
      setTeamPlans([]);
    } finally {
      setTeamLoading(false);
    }
  }, [epicId, showToast]);

  const loadReleasePlans = useCallback(async () => {
    if (!epicId) return;
    setReleaseLoading(true);
    try {
      const data = await epicsApi.listReleasePlans(epicId);
      const next = createInitialReleasePlans();
      (Array.isArray(data) ? data : []).forEach((row) => {
        if (!RELEASE_ENVS.includes(row.environment)) return;
        next[row.environment] = {
          enabled: true,
          planned_release_date: toInputDate(row.planned_release_date),
        };
      });
      setReleasePlans(next);
    } catch (err) {
      showToast?.(err?.message || "Failed to load release plans", "error");
      setReleasePlans(createInitialReleasePlans());
    } finally {
      setReleaseLoading(false);
    }
  }, [epicId, showToast]);

  const loadChecklistItems = useCallback(async () => {
    if (!epicId) return;
    setChecklistLoading(true);
    try {
      const data = await epicsApi.listChecklistItems(epicId);
      setChecklistItems(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast?.(err?.message || "Failed to load checklist items", "error");
      setChecklistItems([]);
    } finally {
      setChecklistLoading(false);
    }
  }, [epicId, showToast]);

  const loadChecklistTemplates = useCallback(async (projectId) => {
    if (!projectId) {
      setTemplates([]);
      return;
    }
    setTemplateLoading(true);
    try {
      const data = await epicsApi.listChecklistTemplates({ project_id: projectId });
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast?.(err?.message || "Failed to load checklist templates", "error");
      setTemplates([]);
    } finally {
      setTemplateLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (!open || !isEdit || !epicId) return;
    epicsApi.listTeams()
      .then((data) => setTeams(Array.isArray(data) ? data : []))
      .catch(() => setTeams([]));
    loadTeamPlans();
    loadReleasePlans();
    loadChecklistItems();
  }, [open, isEdit, epicId, loadChecklistItems, loadReleasePlans, loadTeamPlans]);

  useEffect(() => {
    if (!open) return;
    epicsApi.listTeams()
      .then((data) => setTeams(Array.isArray(data) ? data : []))
      .catch(() => setTeams([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    loadChecklistTemplates(form.project_id);
  }, [open, form.project_id, loadChecklistTemplates]);

  const submit = () => {
    if (readOnly) return;
    if (!validate()) return;
    if (isEdit && epic?.project_id && form.project_id !== epic.project_id) {
      const ok = window.confirm(
        "This will also move all of this epic's tasks to the new project. Continue?"
      );
      if (!ok) return;
    }
    onSave?.({
      project_id: form.project_id,
      epic_name: form.epic_name.trim(),
      description: form.description.trim(),
      recurrence_frequency: form.recurrence_frequency,
      estimated_hours: Number(form.estimated_hours || 0),
      start_date: form.start_date,
      end_date: form.end_date,
      status: form.status,
    });
  };

  const addTeamPlan = async () => {
    if (readOnly) return;
    const err = validateTeamDraft();
    if (err) {
      setTeamError(err);
      return;
    }
    if (!epicId) return;
    setTeamError("");
    setTeamSaving(true);
    try {
      await epicsApi.createTeamPlan(epicId, {
        team_id: teamDraft.team_id,
        start_date: teamDraft.start_date,
        end_date: teamDraft.end_date,
        planned_hours: Number(teamDraft.planned_hours),
        actual_hours: 0,
      });
      setTeamDraft(createInitialTeamDraft());
      await loadTeamPlans();
      showToast?.("Team plan added");
    } catch (err2) {
      setTeamError(err2?.message || "Failed to add team plan");
    } finally {
      setTeamSaving(false);
    }
  };

  const deleteTeamPlan = async (teamPlanId) => {
    if (readOnly) return;
    if (!teamPlanId) return;
    setTeamDeletingId(teamPlanId);
    try {
      await epicsApi.deleteTeamPlan(teamPlanId);
      setTeamPlans((prev) => prev.filter((row) => row.id !== teamPlanId));
      showToast?.("Team plan removed");
    } catch (err) {
      showToast?.(err?.message || "Failed to remove team plan", "error");
    } finally {
      setTeamDeletingId("");
    }
  };

  const saveReleasePlans = async () => {
    if (readOnly) return;
    if (!epicId) return;
    for (const env of RELEASE_ENVS) {
      const cfg = releasePlans[env];
      if (cfg?.enabled && !cfg?.planned_release_date) {
        showToast?.(`Select planned date for ${env}`, "error");
        return;
      }
    }
    setReleaseSaving(true);
    try {
      const payload = RELEASE_ENVS.map((env) => {
        const cfg = releasePlans[env];
        return {
          environment: env,
          enabled: !!cfg?.enabled,
          planned_release_date:
            cfg?.enabled && cfg?.planned_release_date
              ? `${cfg.planned_release_date}T00:00:00`
              : null,
        };
      });
      await epicsApi.upsertReleasePlans(epicId, payload);
      await loadReleasePlans();
      showToast?.("Release plan saved");
    } catch (err) {
      showToast?.(err?.message || "Failed to save release plan", "error");
    } finally {
      setReleaseSaving(false);
    }
  };

  const attachTemplate = async () => {
    if (readOnly) return;
    if (!epicId || !selectedTemplateId) return;
    setTemplateSaving(true);
    try {
      await epicsApi.attachChecklistTemplate(epicId, selectedTemplateId);
      setSelectedTemplateId("");
      await loadChecklistItems();
      showToast?.("Template attached to epic");
    } catch (err) {
      showToast?.(err?.message || "Failed to attach template", "error");
    } finally {
      setTemplateSaving(false);
    }
  };

  const toggleChecklist = async (item) => {
    if (readOnly) return;
    if (!item?.id) return;
    setChecklistUpdatingId(item.id);
    try {
      await epicsApi.updateChecklistItem(item.id, { is_completed: !item.is_completed });
      setChecklistItems((prev) =>
        prev.map((row) => {
          if (row.id !== item.id) return row;
          const nextDone = !row.is_completed;
          return {
            ...row,
            is_completed: nextDone,
            completed_at: nextDone ? new Date().toISOString() : null,
          };
        })
      );
    } catch (err) {
      showToast?.(err?.message || "Failed to update checklist item", "error");
    } finally {
      setChecklistUpdatingId("");
    }
  };

  const tabs = [
    { key: "details", label: "Details", icon: "details" },
    { key: "teams", label: "Team Planning", icon: "users" },
    { key: "release", label: "Release Plan", icon: "release" },
    { key: "checklist", label: "Checklist", icon: "checklist" },
    { key: "attachments", label: "Attachments", icon: "attachments" },
  ];

  return (
    <>
      <SideDrawerShell
        open={open}
        onClose={onClose}
        subtitle={isEdit ? epic?.epic_code || "Epic" : "Epic"}
        title={isEdit ? "Edit Epic" : "New Epic"}
        width="520px"
        tabs={(
          <DrawerTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />
        )}
        footer={(
          <>
            <span className="v2drw-foot-spacer" />
            <button type="button" className="v2drw-btn-cancel" onClick={onClose} disabled={saving}>
              {readOnly ? "Close" : "Cancel"}
            </button>
            {!readOnly ? (
              <button type="button" className="v2drw-btn-save" onClick={submit} disabled={saving}>
                {saving ? "Saving..." : (isEdit ? "Save changes" : "Create epic")}
              </button>
            ) : null}
          </>
        )}
      >
        {activeTab === "details" && (
          <div className="epv2-drawer-form">
            <div className="epv2-drawer-row epv2-drawer-row--full">
              <label className="v2drw-form-label">Project *</label>
              <ComboBox
                options={projectOptions}
                value={form.project_id}
                onChange={(val) => setField("project_id", val)}
                placeholder="Search project…"
                error={!!errors.project_id}
                disabled={readOnly}
              />
              {errors.project_id ? <div className="epv2-err">{errors.project_id}</div> : null}
            </div>

            <div className="epv2-drawer-row epv2-drawer-row--full">
              <label className="v2drw-form-label">Epic Name *</label>
              <input
                className="v2drw-control"
                value={form.epic_name}
                onChange={(e) => setField("epic_name", e.target.value)}
                placeholder="Enter epic name"
                maxLength={300}
                disabled={readOnly}
              />
              {errors.epic_name ? <div className="epv2-err">{errors.epic_name}</div> : null}
            </div>

            <div className="epv2-drawer-row epv2-drawer-row--full">
              <label className="v2drw-form-label">Description</label>
              <textarea
                className="v2drw-control"
                rows={4}
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                placeholder="Optional description"
                disabled={readOnly}
              />
            </div>

            <div className="epv2-drawer-row">
              <label className="v2drw-form-label">Frequency *</label>
              <select
                className="v2drw-control"
                value={form.recurrence_frequency}
                onChange={(e) => setField("recurrence_frequency", e.target.value)}
                disabled={readOnly}
              >
                {FREQUENCY_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {errors.recurrence_frequency ? <div className="epv2-err">{errors.recurrence_frequency}</div> : null}
            </div>

            <div className="epv2-drawer-row">
              <label className="v2drw-form-label">Status *</label>
              <select
                className="v2drw-control"
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
                disabled={readOnly}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {errors.status ? <div className="epv2-err">{errors.status}</div> : null}
            </div>

            <div className="epv2-drawer-row">
              <label className="v2drw-form-label">Estimated Hours *</label>
              <input
                className="v2drw-control"
                type="number"
                min="0"
                step="0.5"
                value={form.estimated_hours}
                onChange={(e) => setField("estimated_hours", e.target.value)}
                disabled={readOnly}
              />
              {errors.estimated_hours ? <div className="epv2-err">{errors.estimated_hours}</div> : null}
            </div>

            <div className="epv2-drawer-row">
              <label className="v2drw-form-label">Start Date *</label>
              <input
                className="v2drw-control"
                type="date"
                value={form.start_date}
                onChange={(e) => setField("start_date", e.target.value)}
                disabled={readOnly}
              />
              {errors.start_date ? <div className="epv2-err">{errors.start_date}</div> : null}
            </div>

            <div className="epv2-drawer-row">
              <label className="v2drw-form-label">End Date *</label>
              <input
                className="v2drw-control"
                type="date"
                value={form.end_date}
                onChange={(e) => setField("end_date", e.target.value)}
                disabled={readOnly}
              />
              {errors.end_date ? <div className="epv2-err">{errors.end_date}</div> : null}
            </div>
          </div>
        )}

        {activeTab === "teams" && (
          <div className="epv2-section">
            {!isEdit && <div className="epv2-info">Save epic first to configure team planning.</div>}
            {isEdit && (
              <>
                <div className="epv2-subhead">
                  <span>Total Planned: {totalPlannedHours}h</span>
                  <span>Epic Estimate: {asNumber(form.estimated_hours)}h</span>
                </div>

                <div className="epv2-team-form">
                  <select
                    className="v2drw-control"
                    value={teamDraft.team_id}
                    onChange={(e) => setTeamDraft((prev) => ({ ...prev, team_id: e.target.value }))}
                    disabled={readOnly}
                  >
                    <option value="">Select team...</option>
                    {teamOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <input
                    className="v2drw-control"
                    type="date"
                    value={teamDraft.start_date}
                    onChange={(e) => setTeamDraft((prev) => ({ ...prev, start_date: e.target.value }))}
                    disabled={readOnly}
                  />
                  <input
                    className="v2drw-control"
                    type="date"
                    value={teamDraft.end_date}
                    onChange={(e) => setTeamDraft((prev) => ({ ...prev, end_date: e.target.value }))}
                    disabled={readOnly}
                  />
                  <input
                    className="v2drw-control"
                    type="number"
                    min="0"
                    step="0.5"
                    placeholder="Hours"
                    value={teamDraft.planned_hours}
                    onChange={(e) => setTeamDraft((prev) => ({ ...prev, planned_hours: e.target.value }))}
                    disabled={readOnly}
                  />
                  <button type="button" className="v2drw-btn-save" onClick={addTeamPlan} disabled={teamSaving || readOnly}>
                    {teamSaving ? "Adding..." : "Add"}
                  </button>
                </div>
                {teamError ? <div className="epv2-err">{teamError}</div> : null}

                <div className="epv2-list">
                  {teamLoading ? (
                    <div className="epv2-empty">Loading team plans...</div>
                  ) : teamPlans.length === 0 ? (
                    <div className="epv2-empty">No team plans yet.</div>
                  ) : (
                    teamPlans.map((row) => (
                      <div className="epv2-list-row" key={row.id}>
                        <div className="epv2-list-main">
                          <strong>{row.team_name || "—"}</strong>
                          <span>{toInputDate(row.start_date)} → {toInputDate(row.end_date)}</span>
                        </div>
                        <div className="epv2-list-side">
                          <span>{asNumber(row.planned_hours)}h</span>
                          <button
                            type="button"
                            className="epv2-link-btn epv2-link-btn--danger"
                            onClick={() => deleteTeamPlan(row.id)}
                            disabled={teamDeletingId === row.id || readOnly}
                          >
                            {teamDeletingId === row.id ? "..." : "Remove"}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === "release" && (
          <div className="epv2-section">
            {!isEdit && <div className="epv2-info">Save epic first to configure release plans.</div>}
            {isEdit && (
              <>
                {releaseLoading ? (
                  <div className="epv2-empty">Loading release plans...</div>
                ) : (
                  <div className="epv2-release-grid">
                    {RELEASE_ENVS.map((env) => (
                      <div key={env} className="epv2-release-row">
                        <label className="epv2-release-toggle">
                          <input
                            type="checkbox"
                            checked={!!releasePlans[env]?.enabled}
                            onChange={(e) => setReleaseField(env, "enabled", e.target.checked)}
                            disabled={readOnly}
                          />
                          <span>{env}</span>
                        </label>
                        <input
                          className="v2drw-control"
                          type="date"
                          disabled={!releasePlans[env]?.enabled || readOnly}
                          value={releasePlans[env]?.planned_release_date || ""}
                          onChange={(e) => setReleaseField(env, "planned_release_date", e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                )}
                <div className="epv2-inline-actions">
                  <button type="button" className="v2drw-btn-save" onClick={saveReleasePlans} disabled={releaseSaving || readOnly}>
                    {releaseSaving ? "Saving..." : "Save Release Plan"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === "checklist" && (
          <div className="epv2-section">
            {!isEdit && <div className="epv2-info">Save epic first to attach checklist templates.</div>}
            {isEdit && (
              <>
                <div className="epv2-team-form">
                  <select
                    className="v2drw-control"
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    disabled={templateLoading || readOnly}
                  >
                    <option value="">Select checklist template...</option>
                    {templates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>
                        {tpl.template_name} [{tpl.scope_type}]
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="v2drw-btn-save"
                    onClick={attachTemplate}
                    disabled={!selectedTemplateId || templateSaving || readOnly}
                  >
                    {templateSaving ? "Attaching..." : "Attach Template"}
                  </button>
                </div>

                <div className="epv2-list">
                  {checklistLoading ? (
                    <div className="epv2-empty">Loading checklist...</div>
                  ) : checklistItems.length === 0 ? (
                    <div className="epv2-empty">No checklist items attached yet.</div>
                  ) : (
                    checklistItems.map((item) => (
                      <div className="epv2-list-row epv2-list-row--checklist" key={item.id}>
                        <label className="epv2-check-item">
                          <input
                            type="checkbox"
                            checked={!!item.is_completed}
                            disabled={checklistUpdatingId === item.id || readOnly}
                            onChange={() => toggleChecklist(item)}
                          />
                          <span>{item.item_text || "—"}</span>
                        </label>
                        <div className="epv2-list-side">
                          <span>{item.is_completed ? `Done · ${fmtDateTime(item.completed_at)}` : "Pending"}</span>
                          <button
                            type="button"
                            className="epv2-link-btn"
                            onClick={() => setAttachmentModalItem(item)}
                          >
                            Attachments
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === "attachments" && (
          <div className="epv2-section">
            {!isEdit && <div className="epv2-info">Save epic first to add attachments.</div>}
            {isEdit && (
              <EntityAttachmentsTab
                refType="epic"
                refId={epicId}
                caption="Epics"
                showToast={showToast}
              />
            )}
          </div>
        )}
      </SideDrawerShell>

      {attachmentModalItem ? (
        <AttachmentsModal
          refType="epic_checklist_item"
          refId={attachmentModalItem.id}
          title={`Checklist Attachments · ${attachmentModalItem.item_text || "Item"}`}
          onClose={() => setAttachmentModalItem(null)}
          defaultCaption="Others"
          lockCaption={false}
          maxFileSizeBytes={2 * 1024 * 1024}
        />
      ) : null}
    </>
  );
}
